import { useEffect, useRef, useState } from "react";
import "@/App.css";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO = "https://customer-assets.emergentagent.com/job_site-main-1/artifacts/m19e3b0x_495B5694-F5A6-47CE-80C0-80F9AB0D25D6%20%281%29.PNG";

/* ------------------------- Scroll Reveal hook ------------------------- */
function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

/* ------------------------- LIVE OPEN STATUS ------------------------- */
function useBamakoStatus() {
  // Bamako = UTC+0. Ouvert Lun-Sam 8h-18h.
  const compute = () => {
    const now = new Date();
    const h = now.getUTCHours();
    const d = now.getUTCDay(); // 0 = dim, 6 = sam
    const open = d >= 1 && d <= 6 && h >= 8 && h < 18;
    return { open, hour: h, day: d };
  };
  const [s, setS] = useState(compute());
  useEffect(() => {
    const id = setInterval(() => setS(compute()), 60000);
    return () => clearInterval(id);
  }, []);
  return s;
}

function OpenBadge({ compact = false }) {
  const { open } = useBamakoStatus();
  return (
    <span className={`open-badge ${open ? "is-open" : "is-closed"} ${compact ? "compact" : ""}`} data-testid="open-badge">
      <span className="ob-dot" />
      {open ? "Ouvert maintenant" : "Fermé · Lun–Sam 8h–18h"}
    </span>
  );
}

/* ------------------------- NAV ------------------------- */
function Nav() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
  }, [open]);

  const links = [
    { href: "#services", label: "Services" },
    { href: "#grands-comptes", label: "Grands comptes" },
    { href: "#marques", label: "Marques" },
    { href: "#about", label: "À propos" },
    { href: "#contact", label: "Contact" },
  ];

  return (
    <>
      <nav className="nav" data-testid="main-nav">
        <a href="#top" className="nav-logo" data-testid="nav-logo-link">
          <img src={LOGO} alt="WBM Solar SARL" />
          <span>WBM <b>Solar</b></span>
        </a>
        <div className="nav-links">
          {links.map((l) => (
            <a key={l.href} href={l.href} className="nav-link" data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}>
              {l.label}
            </a>
          ))}
        </div>
        <a href="#contact" className="nav-cta" data-testid="nav-cta-devis">
          Devis gratuit
          <i className="fa-solid fa-arrow-right" style={{ fontSize: 11 }} />
        </a>
        <button className="nav-burger" onClick={() => setOpen(true)} data-testid="nav-burger" aria-label="Ouvrir le menu">
          <i className="fa-solid fa-bars" />
        </button>
      </nav>

      <div className={`mobile-menu ${open ? "open" : ""}`} data-testid="mobile-menu">
        <button className="mobile-close" onClick={() => setOpen(false)} data-testid="mobile-close" aria-label="Fermer le menu">
          <i className="fa-solid fa-xmark" />
        </button>
        <ul>
          {links.map((l) => (
            <li key={l.href}>
              <a href={l.href} onClick={() => setOpen(false)} data-testid={`mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}>
                {l.label}
              </a>
            </li>
          ))}
          <li>
            <a href="#contact" onClick={() => setOpen(false)} style={{ color: "var(--wbm-gold)" }}>
              Demander un devis →
            </a>
          </li>
        </ul>
      </div>
    </>
  );
}

/* ------------------------- HERO ------------------------- */
function Hero() {
  return (
    <section className="hero" id="top">
      <div className="hero-bg-sun" aria-hidden />
      <div className="wrap hero-grid">
        <div>
          <div className="hero-kicker reveal-item" data-testid="hero-kicker">
            <span className="flag" />
            <span>Bamako · Mali &nbsp;·&nbsp; Énergie permanente</span>
            <OpenBadge compact />
          </div>
          <h1 className="reveal-item">
            L'énergie du <em>soleil</em>,<br />
            <span className="line-2">au service du <u>Mali</u>.</span>
          </h1>
          <p className="hero-sub reveal-item">
            WBM Solar SARL conçoit et installe des systèmes solaires haute puissance pour aéroports, mines, ministères,
            entreprises et foyers — pour une indépendance énergétique totale, jour et nuit.
          </p>
          <div className="hero-ctas reveal-item">
            <a href="#services" className="btn btn-primary" data-testid="hero-cta-primary">
              Voir nos solutions
              <span className="arrow"><i className="fa-solid fa-arrow-right" /></span>
            </a>
            <a href="#grands-comptes" className="btn btn-ghost" data-testid="hero-cta-secondary">
              Grands comptes
            </a>
          </div>

          <div className="hero-meta reveal-item">
            <div className="hero-meta-item">
              <div className="num">300+</div>
              <div className="lb">Installations réalisées</div>
            </div>
            <div className="hero-meta-item">
              <div className="num">5<span style={{ color: "var(--wbm-amber)" }}>+</span></div>
              <div className="lb">Années d'expérience</div>
            </div>
            <div className="hero-meta-item">
              <div className="num">24/7</div>
              <div className="lb">Énergie continue</div>
            </div>
          </div>
        </div>

        <div className="hero-visual-wrap" style={{ position: "relative" }}>
          <div className="hero-orbit" data-testid="hero-orbit-card">
            <div className="ico"><i className="fa-solid fa-bolt" /></div>
            <div>
              <div className="lb">Autonomie</div>
              <strong>-80% sur diesel</strong>
            </div>
          </div>

          <div className="hero-visual">
            <div className="hero-chip" data-testid="hero-chip">
              <i className="fa-solid fa-sun" />
              <span>100% Solaire</span>
            </div>
            <img
              src="https://images.unsplash.com/photo-1466611653911-95081537e5b7?auto=format&fit=crop&w=1200&q=80"
              alt="Panneaux solaires WBM"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------- MARQUEE ------------------------- */
function Marquee() {
  const items = [
    "Aéroports",
    "Mines & industries",
    "Ministères",
    "Hôpitaux",
    "Universités",
    "Pompage agricole",
    "Villas & foyers",
    "PME & usines",
    "Stockage batteries",
    "Énergie permanente",
  ];
  const loop = [...items, ...items];
  return (
    <div className="marquee" data-testid="marquee">
      <div className="marquee-track">
        {loop.map((t, i) => (
          <span className="marquee-item" key={i}>
            <span className="sep" />
            <em style={{ fontStyle: "italic" }}>{t}</em>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ------------------------- SERVICES ------------------------- */
function Services() {
  const services = [
    { icon: "fa-house-chimney", title: "Solaire résidentiel", desc: "Alimentez votre maison avec une énergie propre et économique. Kits adaptés à toutes tailles de foyers.", num: "01" },
    { icon: "fa-industry", title: "Solaire industriel & commercial", desc: "Réduisez vos coûts énergétiques avec des installations haute puissance pour PME, usines et bâtiments commerciaux.", num: "02" },
    { icon: "fa-seedling", title: "Pompage solaire agricole", desc: "Systèmes de pompage pour l'irrigation et l'eau potable — sans groupe électrogène, sans facture d'électricité.", num: "03" },
    { icon: "fa-car-battery", title: "Stockage d'énergie (batteries)", desc: "Solutions SUNNIC/CATL et GSL pour une autonomie maximale, même la nuit ou lors des coupures de courant.", num: "04", wide: true },
    { icon: "fa-screwdriver-wrench", title: "Maintenance & SAV", desc: "Service après-vente réactif, maintenance préventive et dépannage pour garantir la longévité de vos installations.", num: "05" },
    { icon: "fa-ruler-combined", title: "Étude & audit énergétique", desc: "Évaluation gratuite de vos besoins et dimensionnement précis pour une rentabilité optimale.", num: "06" },
  ];
  return (
    <section className="section services-section" id="services">
      <div className="wrap">
        <div className="sec-head reveal">
          <div className="eyebrow"><span className="dot" /> Ce que nous faisons</div>
          <h2>
            Nos <em className="display-italic">solutions solaires</em>
            <br />pensées pour le Mali.
          </h2>
          <p>
            Des installations sur mesure pour chaque besoin, conçues pour durer dans les conditions climatiques du Sahel —
            de la villa familiale aux infrastructures critiques.
          </p>
        </div>

        <div className="services-grid">
          {services.map((s, i) => (
            <article
              key={i}
              className={`service-card reveal ${s.wide ? "wide" : ""}`}
              data-testid={`service-card-${i}`}
              onMouseMove={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                e.currentTarget.style.setProperty("--mx", `${((e.clientX - r.left) / r.width) * 100}%`);
                e.currentTarget.style.setProperty("--my", `${((e.clientY - r.top) / r.height) * 100}%`);
              }}
            >
              <span className="num">{s.num}</span>
              <div>
                <div className="service-icon"><i className={`fa-solid ${s.icon}`} /></div>
                <h3 className="service-title">{s.title}</h3>
                <p className="service-desc">{s.desc}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------- GRANDS COMPTES ------------------------- */
function GrandsComptes() {
  const cards = [
    {
      accent: "#1c6fb3",
      icon: "fa-plane-departure",
      label: "Aéroports",
      title: "Alimentation solaire permanente des aéroports",
      desc:
        "Centrales solaires couplées à de grands systèmes de stockage CATL/SUNNIC pour alimenter en continu terminaux, pistes, tours de contrôle et systèmes de navigation — même en cas de coupure du réseau national.",
      features: [
        { i: "fa-bolt", t: "Puissance MW adaptée à la taille de l'aéroport" },
        { i: "fa-battery-full", t: "Stockage massif pour continuité 24h/24" },
        { i: "fa-satellite-dish", t: "Supervision et télémonitoring temps réel" },
        { i: "fa-shield-halved", t: "Redondance et sécurité électrique totale" },
      ],
    },
    {
      accent: "#f08a1a",
      icon: "fa-helmet-safety",
      label: "Mines & industries extractives",
      title: "Énergie solaire hybride pour sites miniers",
      desc:
        "Les sites miniers consomment énormément et sont souvent isolés du réseau. WBM Solar déploie des solutions hybrides solaire + stockage qui remplacent les groupes électrogènes diesel — économies massives, impact environnemental réduit.",
      features: [
        { i: "fa-coins", t: "Réduction jusqu'à 80% de la consommation diesel" },
        { i: "fa-mountain-sun", t: "Installation sur sites isolés et difficiles d'accès" },
        { i: "fa-gears", t: "Contrats de maintenance sur site inclus" },
        { i: "fa-leaf", t: "Conformité aux exigences ESG et environnementales" },
      ],
    },
    {
      accent: "#2e8a3b",
      icon: "fa-landmark-dome",
      label: "Ministères & institutions publiques",
      title: "Bâtiments gouvernementaux 100% solaire",
      desc:
        "Ministères, hôpitaux, universités — WBM Solar prend en charge l'étude, l'installation et la maintenance pour une transition énergétique complète vers le solaire, sans interruption de service.",
      features: [
        { i: "fa-building-columns", t: "Couverture complète des besoins électriques" },
        { i: "fa-file-signature", t: "Accompagnement pour appels d'offres publics" },
        { i: "fa-plug-circle-check", t: "Zéro coupure grâce au stockage batteries" },
        { i: "fa-chart-line", t: "Retour sur investissement en moins de 5 ans" },
      ],
    },
  ];
  return (
    <section className="section grands-section" id="grands-comptes">
      <div className="wrap">
        <div className="sec-head reveal">
          <div className="eyebrow"><span className="dot" /> Solutions grands comptes</div>
          <h2>
            Aéroports, Mines & <em className="display-italic" style={{ color: "var(--wbm-gold)" }}>Institutions</em>
            <br />— 100% solaire.
          </h2>
          <p>
            WBM Solar conçoit des centrales solaires de grande puissance pour que les infrastructures critiques du Mali
            tournent en permanence sur énergie propre, sans interruption.
          </p>
        </div>

        <div className="grands-grid">
          {cards.map((c, i) => (
            <article
              key={i}
              className="grand-card reveal"
              style={{ "--accent": c.accent }}
              data-testid={`grand-card-${i}`}
            >
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
                <div className="grand-icon"><i className={`fa-solid ${c.icon}`} /></div>
                <div className="grand-label">{c.label}</div>
              </div>
              <h3 className="grand-title">{c.title}</h3>
              <p className="grand-desc">{c.desc}</p>
              <ul className="grand-features">
                {c.features.map((f, j) => (
                  <li key={j}>
                    <i className={`fa-solid ${f.i}`} />
                    <span>{f.t}</span>
                  </li>
                ))}
              </ul>
              <a href="#contact" className="grand-cta">
                Étudier votre projet
                <i className="fa-solid fa-arrow-right" />
              </a>
            </article>
          ))}
        </div>

        <div className="permanence reveal" data-testid="permanence-banner">
          <div className="permanence-icon"><i className="fa-solid fa-sun" /></div>
          <div>
            <h4>Énergie solaire permanente — jour comme nuit</h4>
            <p>
              Grâce au stockage batteries CATL/SUNNIC de grande capacité, vos infrastructures tournent sur énergie solaire
              en continu — même la nuit, par temps nuageux ou lors de pannes du réseau national.
            </p>
          </div>
          <a href="#contact" className="btn btn-primary">
            Nous contacter
            <span className="arrow"><i className="fa-solid fa-arrow-right" /></span>
          </a>
        </div>
      </div>
    </section>
  );
}

/* ------------------------- BRANDS ------------------------- */
function Brands() {
  return (
    <section className="brands-section" id="marques">
      <div className="wrap">
        <div className="sec-head reveal">
          <div className="eyebrow"><span className="dot" /> Nos partenaires technologiques</div>
          <h2>
            Les meilleures <em className="display-italic">marques mondiales</em>.
          </h2>
          <p>
            Nous travaillons exclusivement avec des fabricants leaders pour garantir performance et fiabilité à long terme.
          </p>
        </div>

        <div className="brands-grid">
          {/* SUNNIC featured */}
          <article className="brand-tile featured reveal" data-testid="brand-sunnic">
            <div>
              <div className="brand-name">SUNNIC</div>
              <p className="brand-sub">
                Leader intelligent des stations BESS — intégration PV + Stockage + Charge EV + diagnostic batterie.
              </p>
              <div style={{ marginTop: 20 }}>
                <span className="catl-pill">
                  <i className="fa-solid fa-industry" /> Filiale stratégique de&nbsp;<strong>CATL</strong>&nbsp;&amp;&nbsp;<strong>Xiaomi</strong>
                </span>
              </div>
              <a className="brand-link" href="https://www.isunnic.com/en/" target="_blank" rel="noreferrer">
                Visiter isunnic.com <i className="fa-solid fa-arrow-up-right-from-square" />
              </a>
            </div>
            <span className="brand-tag">BESS · Stockage · Charge EV</span>
          </article>

          {/* CATL tall */}
          <article className="brand-tile tall reveal" data-testid="brand-catl">
            <div>
              <div className="brand-name">CATL</div>
              <p className="brand-sub">
                Nº1 mondial des batteries LiFePO₄ — technologie à la pointe, maison mère de SUNNIC.
              </p>
              <a className="brand-link" href="https://www.catl.com/en/" target="_blank" rel="noreferrer">
                catl.com <i className="fa-solid fa-arrow-up-right-from-square" />
              </a>
            </div>
            <span className="brand-tag">Batteries LiFePO₄</span>
          </article>

          {/* Others */}
          <article className="brand-tile reveal" data-testid="brand-gsl">
            <div>
              <div className="brand-name">GSL</div>
              <p className="brand-sub">Solutions de stockage fiables pour résidentiel et commercial.</p>
              <a className="brand-link" href="https://www.gsl-energy.com/" target="_blank" rel="noreferrer" style={{ color: "var(--wbm-amber-deep)" }}>
                gsl-energy.com <i className="fa-solid fa-arrow-up-right-from-square" />
              </a>
            </div>
            <span className="brand-tag">Stockage énergie</span>
          </article>
          <article className="brand-tile reveal" data-testid="brand-cetc">
            <div>
              <div className="brand-name">CETC</div>
              <p className="brand-sub">Fabricant de panneaux solaires haute performance — modules photovoltaïques fiables pour tous les climats.</p>
              <a className="brand-link" href="http://cetcsolarenergy.com/" target="_blank" rel="noreferrer" style={{ color: "var(--wbm-amber-deep)" }}>
                cetcsolarenergy.com <i className="fa-solid fa-arrow-up-right-from-square" />
              </a>
            </div>
            <span className="brand-tag">Panneaux solaires</span>
          </article>
          <article className="brand-tile reveal" data-testid="brand-sungrow">
            <div>
              <div className="brand-name">SUNGROW</div>
              <p className="brand-sub">Leader mondial des onduleurs — 405+ GW installés dans 150+ pays.</p>
              <a className="brand-link" href="https://www.sungrowpower.com/en" target="_blank" rel="noreferrer" style={{ color: "var(--wbm-amber-deep)" }}>
                sungrowpower.com <i className="fa-solid fa-arrow-up-right-from-square" />
              </a>
            </div>
            <span className="brand-tag">Onduleurs</span>
          </article>
          <article className="brand-tile reveal" data-testid="brand-deye">
            <div>
              <div className="brand-name">DEYE</div>
              <p className="brand-sub">Onduleurs hybrides performants — on-grid, off-grid et systèmes couplés au réseau.</p>
              <a className="brand-link" href="https://deye.com/" target="_blank" rel="noreferrer" style={{ color: "var(--wbm-amber-deep)" }}>
                deye.com <i className="fa-solid fa-arrow-up-right-from-square" />
              </a>
            </div>
            <span className="brand-tag">Onduleurs hybrides</span>
          </article>
          <article className="brand-tile reveal" data-testid="brand-growatt">
            <div>
              <div className="brand-name">GROWATT</div>
              <p className="brand-sub">Solutions photovoltaïques complètes pour zones off-grid et marchés isolés.</p>
              <a className="brand-link" href="https://en.growatt.com/" target="_blank" rel="noreferrer" style={{ color: "var(--wbm-amber-deep)" }}>
                growatt.com <i className="fa-solid fa-arrow-up-right-from-square" />
              </a>
            </div>
            <span className="brand-tag">Solutions off-grid</span>
          </article>
        </div>
      </div>
    </section>
  );
}

/* ------------------------- WHY ------------------------- */
function Why() {
  const items = [
    { n: "01", i: "fa-bolt-lightning", t: "Installation rapide & professionnelle", p: "Techniciens certifiés, délais respectés, équipements de qualité internationale — du devis à la mise en service." },
    { n: "02", i: "fa-sun-plant-wilt", t: "Expertise locale du climat malien", p: "Nous dimensionnons chaque système pour maximiser la production solaire toute l'année dans les conditions du Sahel." },
    { n: "03", i: "fa-hand-holding-dollar", t: "Prix compétitifs & paiement flexible", p: "Solutions accessibles pour tous les budgets avec des options de financement adaptées aux projets importants." },
    { n: "04", i: "fa-shield-halved", t: "Garantie & suivi longue durée", p: "Garantie fabricant sur tous nos systèmes avec suivi post-installation pour votre tranquillité d'esprit." },
  ];
  return (
    <section className="section why-section" id="about">
      <div className="wrap">
        <div className="sec-head reveal">
          <div className="eyebrow"><span className="dot" /> Pourquoi WBM Solar</div>
          <h2>
            Une équipe <em className="display-italic">de confiance</em><br />
            installée au Mali.
          </h2>
        </div>

        <div className="why-grid">
          <div className="why-list reveal">
            {items.map((it, i) => (
              <div key={i} className="why-item" data-testid={`why-item-${i}`}>
                <div className="n">{it.n}</div>
                <div>
                  <h4>{it.t}</h4>
                  <p>{it.p}</p>
                </div>
              </div>
            ))}
          </div>

          <div style={{ position: "relative" }} className="reveal">
            <div className="why-visual">
              <img
                src="https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?auto=format&fit=crop&w=900&q=80"
                alt="Installation solaire au Mali"
              />
            </div>
            <div className="why-badge top-right">
              <span className="v">25 ans</span>
              <span className="l">Durée panneaux</span>
            </div>
            <div className="why-badge mid-left">
              <span className="v">-80%</span>
              <span className="l">Sur vos factures</span>
            </div>
            <div className="why-badge bot-right">
              <span className="v">0 CO₂</span>
              <span className="l">Énergie propre</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------- PROCESS ------------------------- */
function Process() {
  const steps = [
    { n: "01", t: "Étude gratuite", p: "Audit de vos besoins énergétiques et visite du site sans engagement." },
    { n: "02", t: "Devis sur mesure", p: "Dimensionnement précis et proposition adaptée à votre budget." },
    { n: "03", t: "Installation", p: "Équipe certifiée, matériel premium et mise en service rapide." },
    { n: "04", t: "Suivi & SAV", p: "Maintenance préventive et support réactif pour tranquillité totale." },
  ];
  return (
    <section className="section process-section" id="process">
      <div className="wrap">
        <div className="sec-head reveal">
          <div className="eyebrow"><span className="dot" /> Comment ça marche</div>
          <h2>
            De l'idée à la mise en <em className="display-italic">service</em>,
            <br />en 4 étapes simples.
          </h2>
        </div>
        <div className="process-grid">
          {steps.map((s, i) => (
            <div key={i} className="process-step reveal" data-testid={`process-step-${i}`}>
              <div className="step-num">{s.n}</div>
              <h4>{s.t}</h4>
              <p>{s.p}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------- TESTIMONIAL ------------------------- */
function Testimonial() {
  return (
    <section className="testi-section">
      <div className="wrap">
        <div className="testi-card reveal" data-testid="testimonial-card">
          <div className="testi-quote">“</div>
          <blockquote>
            Grâce à WBM Solar, notre site tourne <em>24h/24</em> sur énergie solaire. Nous avons réduit de{" "}
            <em>75%</em> notre consommation de diesel en moins d'un an — une transformation remarquable pour notre activité.
          </blockquote>
          <div className="testi-author">
            <div className="testi-avatar">AT</div>
            <div>
              <div className="testi-author-name">Amadou Traoré</div>
              <div className="testi-author-role">Directeur opérations, site industriel · Bamako</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------- CONTACT ------------------------- */
function Contact() {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    project_type: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null); // {ok, text}

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);
    try {
      await axios.post(`${API}/contact`, form);
      setStatus({ ok: true, text: "✓ Merci ! Notre équipe vous recontacte sous 24h." });
      setForm({ first_name: "", last_name: "", phone: "", email: "", project_type: "", message: "" });
    } catch (err) {
      setStatus({ ok: false, text: "Une erreur est survenue. Merci de réessayer ou de nous appeler directement." });
    } finally {
      setLoading(false);
    }
  };

  const up = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <section className="contact-section" id="contact">
      <div className="wrap">
        <div className="sec-head reveal" style={{ maxWidth: 820 }}>
          <div className="eyebrow"><span className="dot" /> Contactez-nous</div>
          <h2>
            Obtenez votre <em className="display-italic" style={{ color: "var(--wbm-gold)" }}>devis gratuit</em>.
          </h2>
          <p>Notre équipe vous répond dans les 24h pour étudier votre projet solaire — quelle que soit son échelle.</p>
        </div>

        <div className="contact-grid">
          <div className="contact-info reveal">
            <h3>WBM Solar SARL</h3>

            <div className="contact-item" data-testid="contact-address">
              <div className="c-ico"><i className="fa-solid fa-location-dot" /></div>
              <div>
                <strong>Adresse</strong>
                <p>Immeuble SAMAKO, Hamdallaye ACI 2000<br />Bamako, Mali</p>
              </div>
            </div>

            <div className="contact-item" data-testid="contact-phone">
              <div className="c-ico"><i className="fa-solid fa-phone" /></div>
              <div>
                <strong>Téléphone</strong>
                <p>
                  <a href="tel:+22373345878" style={{ color: "inherit" }}>+223 73 34 58 78</a>
                  {" · "}
                  <a href="tel:+22372729797" style={{ color: "inherit" }}>+223 72 72 97 97</a>
                </p>
              </div>
            </div>

            <div className="contact-item" data-testid="contact-email">
              <div className="c-ico"><i className="fa-solid fa-envelope" /></div>
              <div>
                <strong>Email</strong>
                <p><a href="mailto:wbmsolar@gmail.com" style={{ color: "inherit" }}>wbmsolar@gmail.com</a></p>
              </div>
            </div>

            <div className="contact-item" data-testid="contact-whatsapp">
              <div className="c-ico"><i className="fa-brands fa-whatsapp" /></div>
              <div>
                <strong>WhatsApp</strong>
                <p><a href="https://wa.me/22372729797" target="_blank" rel="noreferrer" style={{ color: "inherit" }}>+223 72 72 97 97 — 7j/7</a></p>
              </div>
            </div>

            <div className="contact-item" data-testid="contact-hours">
              <div className="c-ico"><i className="fa-regular fa-clock" /></div>
              <div>
                <strong>Horaires</strong>
                <p>Lun – Sam : 8h00 – 18h00 &nbsp;·&nbsp; <OpenBadge /></p>
              </div>
            </div>

            <div className="contact-map" data-testid="contact-map">
              <iframe
                title="WBM Solar — Hamdallaye ACI 2000, Bamako"
                src="https://www.google.com/maps?q=Hamdallaye+ACI+2000,+Bamako,+Mali&output=embed"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <a
                className="map-open"
                href="https://www.google.com/maps/search/?api=1&query=Immeuble+SAMAKO+Hamdallaye+ACI+2000+Bamako+Mali"
                target="_blank"
                rel="noreferrer"
                data-testid="map-open-link"
              >
                Ouvrir dans Google Maps <i className="fa-solid fa-arrow-up-right-from-square" />
              </a>
            </div>

            <div className="contact-socials">
              <a href="#" aria-label="Facebook" data-testid="social-facebook"><i className="fa-brands fa-facebook-f" /></a>
              <a href="#" aria-label="LinkedIn" data-testid="social-linkedin"><i className="fa-brands fa-linkedin-in" /></a>
              <a href="#" aria-label="Instagram" data-testid="social-instagram"><i className="fa-brands fa-instagram" /></a>
              <a href="https://wa.me/22372729797" target="_blank" rel="noreferrer" aria-label="WhatsApp" data-testid="social-whatsapp"><i className="fa-brands fa-whatsapp" /></a>
            </div>
          </div>

          <form className="contact-form reveal" onSubmit={onSubmit} data-testid="contact-form">
            <div className="form-row">
              <div className="form-group">
                <label>Prénom</label>
                <input type="text" placeholder="Votre prénom" required value={form.first_name} onChange={up("first_name")} data-testid="input-first-name" />
              </div>
              <div className="form-group">
                <label>Nom</label>
                <input type="text" placeholder="Votre nom" required value={form.last_name} onChange={up("last_name")} data-testid="input-last-name" />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Téléphone / WhatsApp</label>
                <input type="tel" placeholder="+223 73 34 58 78" required value={form.phone} onChange={up("phone")} data-testid="input-phone" />
              </div>
              <div className="form-group">
                <label>Email (optionnel)</label>
                <input type="email" placeholder="vous@exemple.com" value={form.email} onChange={up("email")} data-testid="input-email" />
              </div>
            </div>

            <div className="form-group">
              <label>Type de projet</label>
              <select required value={form.project_type} onChange={up("project_type")} data-testid="input-project-type">
                <option value="">Sélectionnez votre projet...</option>
                <option>Résidentiel (maison / villa)</option>
                <option>Commercial / Entreprise</option>
                <option>Pompage agricole</option>
                <option>Industriel</option>
                <option>Aéroport / infrastructure publique</option>
                <option>Site minier</option>
                <option>Stockage batteries</option>
                <option>Autre</option>
              </select>
            </div>

            <div className="form-group">
              <label>Message</label>
              <textarea
                placeholder="Décrivez votre projet (localisation, superficie, besoins électriques...)"
                required
                value={form.message}
                onChange={up("message")}
                data-testid="input-message"
              />
            </div>

            <button type="submit" className="submit-btn" disabled={loading} data-testid="submit-btn">
              <i className="fa-solid fa-sun" />
              {loading ? "Envoi en cours..." : "Envoyer ma demande de devis"}
            </button>

            {status && (
              <div className={`form-status ${status.ok ? "ok" : "err"}`} data-testid="form-status">
                {status.text}
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}

/* ------------------------- FOOTER ------------------------- */
function Footer() {
  return (
    <footer className="footer">
      <div className="wrap">
        <div className="footer-grid">
          <div className="footer-brand">
            <div className="footer-logo">
              <img src={LOGO} alt="WBM Solar SARL" />
              <span>WBM Solar <span style={{ color: "var(--wbm-amber)" }}>SARL</span></span>
            </div>
            <p className="footer-about">
              Énergie solaire haute puissance au Mali. Installation, stockage et maintenance pour foyers, entreprises,
              mines, aéroports et institutions publiques.
            </p>
          </div>

          <div className="footer-col">
            <h5>Solutions</h5>
            <ul>
              <li><a href="#services">Résidentiel</a></li>
              <li><a href="#services">Industriel</a></li>
              <li><a href="#services">Pompage agricole</a></li>
              <li><a href="#grands-comptes">Grands comptes</a></li>
            </ul>
          </div>

          <div className="footer-col">
            <h5>Entreprise</h5>
            <ul>
              <li><a href="#about">À propos</a></li>
              <li><a href="#marques">Marques partenaires</a></li>
              <li><a href="#process">Notre process</a></li>
              <li><a href="#contact">Contact</a></li>
            </ul>
          </div>

          <div className="footer-col">
            <h5>Contact</h5>
            <ul>
              <li><a href="tel:+22373345878">+223 73 34 58 78</a></li>
              <li><a href="tel:+22372729797">+223 72 72 97 97</a></li>
              <li><a href="mailto:wbmsolar@gmail.com">wbmsolar@gmail.com</a></li>
              <li>Immeuble SAMAKO</li>
              <li>Hamdallaye ACI 2000</li>
              <li>Bamako, Mali</li>
              <li>Lun – Sam : 8h – 18h</li>
            </ul>
          </div>
        </div>

        <div className="footer-legal" data-testid="footer-legal">
          <div className="legal-item"><span>RCN°</span><strong>MA BKO 2024BSS41</strong></div>
          <div className="legal-item"><span>NIF</span><strong>087100079N</strong></div>
          <div className="legal-item"><span>Compte BAM</span><strong>72912940004</strong></div>
          <div className="legal-item"><span>Compte BMS</span><strong>7259902001</strong></div>
        </div>

        <div className="footer-bottom">
          <p>© 2025 <strong>WBM Solar SARL</strong> — Tous droits réservés · <a href="https://wbmsolarmali.com" style={{ color: "var(--wbm-amber)" }}>wbmsolarmali.com</a> · Bamako, Mali</p>
          <p>Fait avec <span style={{ color: "var(--wbm-amber)" }}>☀</span> pour un Mali plus vert</p>
        </div>
      </div>
    </footer>
  );
}

/* ------------------------- APP ------------------------- */
function App() {
  useReveal();
  useEffect(() => {
    // lightweight health ping (non-blocking)
    axios.get(`${API}/`).catch(() => {});
  }, []);

  return (
    <div className="App">
      <Nav />
      <Hero />
      <Marquee />
      <Services />
      <GrandsComptes />
      <Brands />
      <Why />
      <Process />
      <Testimonial />
      <Contact />
      <Footer />
      <a
        className="wa-float"
        href="https://wa.me/22372729797?text=Bonjour%20WBM%20Solar%2C%20je%20souhaite%20un%20devis."
        target="_blank"
        rel="noreferrer"
        aria-label="Discuter sur WhatsApp"
        data-testid="wa-float-btn"
      >
        <i className="fa-brands fa-whatsapp" />
      </a>
    </div>
  );
}

export default App;
